/**
 * useNavigationState Hook
 * 
 * Provides centralized navigation state management for active route detection.
 * 
 * Features:
 * - Detects if any item in a section matches the current route
 * - Provides utilities for checking active routes
 * - Optimized with memoization for array iterations
 * 
 * @param items - Array of navigation items to check against current route
 * 
 * @example
 * const { isActiveSection, currentPath } = useNavigationState(items);
 * 
 * // Check if any Learn item is active
 * if (isActiveSection) {
 *   // Highlight the Learn section
 * }
 */

import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { NavigationItem } from "../types/navigation";

export function useNavigationState(items: readonly NavigationItem[]) {
    const location = useLocation();

    // Memoized - iterates over array, worth caching
    const isActiveSection = useMemo(() => {
        return items.some((item) => location.pathname.startsWith(item.to));
    }, [items, location.pathname]);

    // Simple string comparison, could skip memoization but kept for consistency
    const isActiveRoute = useMemo(() => {
        return (route: string) => location.pathname === route;
    }, [location.pathname]);

    const getActiveItemIndex = useMemo(() => {
        return items.findIndex((item) => location.pathname.startsWith(item.to));
    }, [items, location.pathname]);

    return {
        isActiveSection,
        isActiveRoute,
        getActiveItemIndex,
        currentPath: location.pathname,
    };
}
